import { memo, useCallback, useState } from 'react';
import { LineChart as GiftedLine } from 'react-gifted-charts';
import { type LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { colors } from '../../theme/colors';
import { ChartTooltip } from './ChartTooltip';
import { chartTheme } from './chart-theme';
import type { DataPoint, LineChartProps } from './types';

export type { LineChartProps };
export type { DataPoint };

interface PointerItem {
  value: number;
  tooltipLabel?: string;
}

// Gap before the first point. `adjustToWidth` subtracts this when it fits the
// series, so the two have to agree.
const INITIAL_SPACING = 8;
// Room for the last x-axis label. The final point sits at
// chartContentWidth - END_SPACING, and its label is centred on it, so at zero
// the label runs off the card and gets sliced down the middle.
const END_SPACING = 24;
// Below this the points sit too close to read, so the chart scrolls instead.
const MIN_POINT_SPACING = 20;

function LineChartInner({
  data,
  width: propWidth,
  height = 200,
  color = colors.primary.neon,
  showArea = true,
  showDots = false,
  showGrid = true,
  showLabels = true,
  showYAxisLabels = true,
  animate = true,
  gridLineCount = 5,
  areaOpacity = 0.2,
  strokeWidth = 2,
  dotRadius = 3,
  style,
}: Readonly<LineChartProps>) {
  const [measuredWidth, setMeasuredWidth] = useState(propWidth ?? 0);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (!propWidth) {
        setMeasuredWidth(e.nativeEvent.layout.width);
      }
    },
    [propWidth],
  );

  const effectiveWidth = propWidth ?? measuredWidth;

  if (!data || data.length < 2) {
    return <View style={[{ width: effectiveWidth || '100%', height }, style]} />;
  }

  const giftedData = data.map((point) => ({
    value: point.value,
    label: point.label,
  }));

  const labelStep = Math.max(1, Math.floor(data.length / 5));
  const giftedDataWithLabels = giftedData.map((item, index) => ({
    ...item,
    tooltipLabel: item.label,
    label: index % labelStep === 0 || index === data.length - 1 ? item.label : '',
  }));

  const yAxisWidth = showLabels && showYAxisLabels ? 40 : 0;
  const chartContentWidth = effectiveWidth > 0 ? effectiveWidth - yAxisWidth - 12 : 0;

  // `adjustToWidth` already fits the series to `width`, but only when we do not
  // hand it a `spacing` of our own — an explicit spacing wins over the fitted
  // one. The previous value divided the full content width between the gaps
  // and ignored initialSpacing/endSpacing entirely, so the plot always came out
  // wider than the card and ran off its right edge.
  //
  // So: let the library fit, and override only when fitting would squeeze the
  // points below a readable gap. Then the chart scrolls, which is the correct
  // outcome for a dense series and the reason the scroll container exists.
  // Spacing is sized so the LAST point lands on the right edge — the plot has
  // to fill the card, not stop short of it. That means dividing by the number
  // of gaps, (n - 1).
  //
  // The library then sizes its own wrapper as spacing * n, one gap wider than
  // the plot needs, and that wrapper is what used to run past the card. The
  // trailing gap holds nothing, so the wrapper below clips it rather than us
  // shrinking the plot to accommodate it. Dropping this explicit spacing and
  // relying on `adjustToWidth` alone was measured and is WORSE (a 1660px
  // wrapper against 1612px in an 830px card): this version of the library does
  // not fit the series on its own.
  const fittedSpacing =
    data.length > 1
      ? (chartContentWidth - INITIAL_SPACING - END_SPACING) / (data.length - 1)
      : chartContentWidth;
  const spacing = Math.max(MIN_POINT_SPACING, fittedSpacing);

  const chartLabel = `Line chart: ${data.length} data points`;

  return (
    <View
      style={[styles.host, style]}
      onLayout={onLayout}
      accessibilityRole="image"
      accessibilityLabel={chartLabel}
    >
      {effectiveWidth > 0 && (
        <GiftedLine
          data={giftedDataWithLabels}
          width={chartContentWidth}
          height={height}
          color={color}
          thickness={strokeWidth}
          isAnimated={animate}
          animateOnDataChange={animate}
          animationDuration={600}
          hideDataPoints={!showDots}
          dataPointsColor={color}
          dataPointsRadius={dotRadius}
          areaChart={showArea}
          startFillColor={color}
          endFillColor={color}
          startOpacity={areaOpacity}
          endOpacity={0}
          hideRules={!showGrid}
          rulesColor={chartTheme.rulesColor}
          rulesType={chartTheme.rulesType}
          noOfSections={gridLineCount}
          yAxisColor={chartTheme.yAxisColor}
          xAxisColor={chartTheme.xAxisColor}
          hideYAxisText={!showYAxisLabels}
          yAxisTextStyle={showLabels ? chartTheme.yAxisTextStyle : { color: 'transparent' }}
          xAxisLabelTextStyle={
            showLabels ? chartTheme.xAxisLabelTextStyle : { color: 'transparent' }
          }
          backgroundColor={chartTheme.backgroundColor}
          curved
          spacing={spacing}
          initialSpacing={INITIAL_SPACING}
          endSpacing={END_SPACING}
          adjustToWidth
          pointerConfig={{
            pointerStripUptoDataPoint: true,
            pointerStripColor: chartTheme.pointerStripColor,
            pointerStripWidth: 1,
            pointerColor: color,
            radius: 5,
            pointerLabelWidth: 120,
            pointerLabelHeight: 56,
            pointerLabelComponent: (items: PointerItem[]) => (
              <ChartTooltip
                value={items[0]?.value ?? 0}
                label={items[0]?.tooltipLabel || undefined}
                accentColor={color}
              />
            ),
          }}
        />
      )}
    </View>
  );
}

export const LineChart = memo(LineChartInner);

const styles = StyleSheet.create({
  // Clips the trailing gap the charting library adds beyond the plot. Without
  // it that overhang is real layout width and the chart runs past its card.
  host: {
    flex: 1,
    maxWidth: '100%',
    overflow: 'hidden',
  },
});
